import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { IEnrollment } from '@dtos/Enrollment'
import { useNewPathEnrollment } from '@services/api/graphql/path'
import {
  ADMIN_PREVIEW_PATH_PAGE_SIZE,
  toPreviewPathEnrollment,
  useAdminPreviewPath,
} from '@services/api/graphql/adminPreviewPath'
import { useLocalSearchParams } from 'expo-router'
import { useApolloErrorToast } from '@hooks/useApolloErrorToast'

export interface IEnrollmentData extends IEnrollment {}
interface IPathContextProviderProps {
  enrollment?: IEnrollmentData | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  newEnrollment: any
  enrollmentIsLoading: boolean
  error?: Error
  onLoadMore: () => Promise<void>
  hasMore: boolean
}

export const PathContext = createContext<IPathContextProviderProps>(
  {} as IPathContextProviderProps,
)

export function PathProvider({ children }: { children: ReactNode }) {
  const [isEmptyPage, setIsEmptyPage] = useState(false)
  const showApolloErrorToast = useApolloErrorToast()
  const { enrollmentId } = useLocalSearchParams()

  const shouldSkipQuery =
    !enrollmentId || Array.isArray(enrollmentId) || enrollmentId === 'undefined'

  const {
    data: newEnrollmentData,
    loading: newEnrollmentLoading,
    error: newEnrollmentError,
    called: calledNewEnrollment,
    getNewPathEnrollment,
  } = useNewPathEnrollment(Number(enrollmentId), 0, 10, {
    skip: shouldSkipQuery,
  })

  const enrollmentIsLoading = useMemo(() => {
    return (
      !newEnrollmentData?.enrollment?.id ||
      (newEnrollmentLoading && !calledNewEnrollment)
    )
  }, [
    newEnrollmentLoading,
    calledNewEnrollment,
    newEnrollmentData?.enrollment?.id,
  ])

  // Sem isso, um empty_page de um path contamina o próximo: hasMore fica false
  // para o resto da sessão e o combo seguinte não pagina.
  useEffect(() => {
    setIsEmptyPage(false)
  }, [enrollmentId])

  useEffect(() => {
    if (newEnrollmentError && !shouldSkipQuery && !isEmptyPage) {
      showApolloErrorToast(
        newEnrollmentError,
        'apolloErrors.enrollmentIdsInfo.title',
      )
    }
  }, [shouldSkipQuery, isEmptyPage, newEnrollmentError, showApolloErrorToast])

  const loadMoreCourses = useCallback(async () => {
    if (isEmptyPage) return

    const { errors } = await getNewPathEnrollment({
      variables: {
        id: Number(enrollmentId),
        offset:
          newEnrollmentData?.enrollment?.progressCard.courseProgressCards
            ?.length,
        limit: 10,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult

        return {
          ...previousResult,
          enrollment: {
            ...previousResult.enrollment,
            progressCard: {
              ...previousResult.enrollment.progressCard,
              courseProgressCards: [
                ...(previousResult.enrollment.progressCard
                  ?.courseProgressCards || []),
                ...(fetchMoreResult.enrollment.progressCard
                  ?.courseProgressCards || []),
              ],
            },
          },
        }
      },
    })

    if (errors?.[0]?.extensions?.code === 'empty_page') {
      setIsEmptyPage(true)
    }
  }, [
    isEmptyPage,
    getNewPathEnrollment,
    enrollmentId,
    newEnrollmentData?.enrollment?.progressCard.courseProgressCards?.length,
  ])

  const noopLoadMore = useCallback(() => Promise.resolve(), [])

  const contextValue = useMemo(
    () => ({
      newEnrollment: newEnrollmentData,
      enrollmentIsLoading,
      error: newEnrollmentError,
      onLoadMore: enrollmentIsLoading ? noopLoadMore : loadMoreCourses,
      hasMore: !isEmptyPage,
    }),
    [
      newEnrollmentData,
      enrollmentIsLoading,
      newEnrollmentError,
      noopLoadMore,
      loadMoreCourses,
      isEmptyPage,
    ],
  )

  return (
    <PathContext.Provider value={contextValue}>{children}</PathContext.Provider>
  )
}

/**
 * Mesma tela da trilha do aluno, alimentada pela trilha em si em vez da
 * matrícula — o produtor em preview não tem nenhuma (HET-3955). Entrega o
 * contexto no formato do PathProvider para o PathContent não saber a diferença.
 */
export function AdminPreviewPathProvider({
  children,
}: {
  children: ReactNode
}) {
  const [hasMore, setHasMore] = useState(true)
  const showApolloErrorToast = useApolloErrorToast()
  const { pathId } = useLocalSearchParams()

  const shouldSkipQuery = !pathId || Array.isArray(pathId)

  const { data, loading, error, fetchMore } = useAdminPreviewPath(
    Number(pathId),
    { skip: shouldSkipQuery },
  )

  const path = data?.adminPreviewPath
  const loadedCoursesCount = path?.coursesInOrder.length ?? 0

  useEffect(() => {
    setHasMore(true)
  }, [pathId])

  // A primeira página curta já diz que não há mais cursos; sem isso o slider
  // pediria uma segunda página vazia.
  useEffect(() => {
    if (path && loadedCoursesCount < ADMIN_PREVIEW_PATH_PAGE_SIZE) {
      setHasMore(false)
    }
  }, [path, loadedCoursesCount])

  useEffect(() => {
    if (error && !shouldSkipQuery) {
      showApolloErrorToast(error, 'apolloErrors.enrollmentIdsInfo.title')
    }
  }, [error, shouldSkipQuery, showApolloErrorToast])

  const loadMoreCourses = useCallback(async () => {
    if (!hasMore || !path) return

    const { data: nextPage } = await fetchMore({
      variables: {
        id: Number(pathId),
        offset: loadedCoursesCount,
        limit: ADMIN_PREVIEW_PATH_PAGE_SIZE,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!previousResult.adminPreviewPath || !fetchMoreResult) {
          return previousResult
        }

        return {
          adminPreviewPath: {
            ...previousResult.adminPreviewPath,
            coursesInOrder: [
              ...previousResult.adminPreviewPath.coursesInOrder,
              ...(fetchMoreResult.adminPreviewPath?.coursesInOrder ?? []),
            ],
          },
        }
      },
    })

    const nextCount = nextPage?.adminPreviewPath?.coursesInOrder.length ?? 0
    if (nextCount < ADMIN_PREVIEW_PATH_PAGE_SIZE) setHasMore(false)
  }, [hasMore, path, fetchMore, pathId, loadedCoursesCount])

  const noopLoadMore = useCallback(() => Promise.resolve(), [])
  const isLoading = !shouldSkipQuery && loading && !path

  const contextValue = useMemo(
    () => ({
      newEnrollment: path ? toPreviewPathEnrollment(path) : undefined,
      enrollmentIsLoading: isLoading,
      error,
      onLoadMore: isLoading ? noopLoadMore : loadMoreCourses,
      hasMore,
    }),
    [path, isLoading, error, noopLoadMore, loadMoreCourses, hasMore],
  )

  return (
    <PathContext.Provider value={contextValue}>{children}</PathContext.Provider>
  )
}
